/**
 * Safe client-side redirect helpers.
 *
 * Wraps `window.location.href = ...` with a strict validator so user- or
 * third-party-controlled URLs cannot be used to deliver XSS (e.g.
 * `javascript:` URIs) or open-redirect attacks.
 *
 * Hostnames may be supplied as either an exact match (`connect.data.one`)
 * or a single-label wildcard (`*.embarknext.com`, which matches any
 * direct or indirect subdomain — never the apex itself).
 *
 * See also: lib/security/__tests__/safeRedirect.test.ts
 */

const SAFE_SCHEMES = new Set(['http:', 'https:']);

// Path characters that signal an attempted protocol-relative or
// backslash-smuggling URL when present right after a leading `/`.
const RELATIVE_SECOND_CHAR_BLOCKLIST = new Set(['/', '\\']);

export interface SafeRedirectOptions {
  /**
   * Hostnames the URL may resolve to. Each entry is either an exact host
   * (e.g. `connect.data.one`) or a single-label wildcard
   * (e.g. `*.embarknext.com`). Matching is case-insensitive.
   */
  allowedHosts?: readonly string[];
  /**
   * When true, only same-origin relative paths (`/foo?bar`) are accepted.
   * Absolute URLs — even to allow-listed hosts — are rejected.
   */
  sameOriginOnly?: boolean;
  /**
   * Allow `http:` URLs. Off by default; only enable in development.
   */
  allowInsecure?: boolean;
}

/**
 * Returns true when `url` is safe to assign to `window.location.href`
 * under the supplied options. Never throws.
 */
export function isSafeRedirectUrl(
  url: unknown,
  opts: SafeRedirectOptions = {},
): boolean {
  if (typeof url !== 'string' || url.length === 0) return false;
  // Reject any control characters or whitespace — these can hide a
  // scheme prefix from the URL parser.
  if (/[\u0000-\u001F\u007F\s]/.test(url)) return false;

  // Relative path: must begin with a single `/` and the next character
  // must not be `/` or `\` (which would make it protocol-relative or
  // backslash-smuggled). A bare `/` is fine.
  if (url.startsWith('/')) {
    if (url.length > 1 && RELATIVE_SECOND_CHAR_BLOCKLIST.has(url[1])) {
      return false;
    }
    return true;
  }

  if (opts.sameOriginOnly) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (!SAFE_SCHEMES.has(parsed.protocol)) return false;
  if (parsed.protocol === 'http:' && !opts.allowInsecure) return false;
  // Embedded credentials are a classic phishing vector.
  if (parsed.username !== '' || parsed.password !== '') return false;

  const allowed = opts.allowedHosts ?? [];
  if (allowed.length === 0) return false;
  return matchesAllowedHost(parsed.hostname, allowed);
}

/**
 * Case-insensitive match of `host` against a mixed list of exact hosts
 * and `*.example.com` wildcards. Wildcards never match the apex.
 */
export function matchesAllowedHost(
  host: string,
  allowedHosts: readonly string[],
): boolean {
  const target = host.toLowerCase();
  for (const entry of allowedHosts) {
    const candidate = entry.trim().toLowerCase();
    if (candidate.length === 0) continue;
    if (candidate.startsWith('*.')) {
      const suffix = candidate.slice(1); // ".embarknext.com"
      if (target.endsWith(suffix) && target.length > suffix.length) {
        return true;
      }
    } else if (target === candidate) {
      return true;
    }
  }
  return false;
}

/**
 * Validate `url` and, when safe, perform a hard navigation by assigning
 * `window.location.href`. Returns true on success, false if the URL was
 * rejected (in which case `onUnsafe` is invoked, if supplied).
 *
 * No-op outside the browser (SSR / Node).
 */
export function safeAssignLocation(
  url: unknown,
  opts: SafeRedirectOptions = {},
  onUnsafe?: (rawUrl: unknown) => void,
): boolean {
  if (!isSafeRedirectUrl(url, opts)) {
    onUnsafe?.(url);
    return false;
  }
  if (typeof window === 'undefined') return false;
  window.location.href = url as string;
  return true;
}
